"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "./LanguageProvider";
import { DealershipMap } from "./DealershipMap";

// Facts from Bilspektrum's own "om oss" text – don't add claims that aren't sourced.
const FACTS = [
  { value: "1973", label: { sv: "Grundat av Åke och Lars Oscarsson", en: "Founded by Åke and Lars Oscarsson" } },
  { value: "50+", label: { sv: "års erfarenhet i branschen", en: "years of experience in the industry" } },
  { value: "Eric & Per", label: { sv: "Bröderna Oscarsson äger och driver företaget i dag", en: "The Oscarsson brothers own and run the company today" } },
  { value: "Mazda & Hyundai", label: { sv: "Återförsäljare av nya bilar från båda märkena", en: "Dealer for new cars from both brands" } },
];

const EASE = [0.22, 1, 0.36, 1] as const;

export const AboutSection = () => {
  const { language } = useLanguage();
  const sv = language === "sv";

  return (
    <section id="om-oss" className="bg-background py-20 lg:py-28">
      <div className="container mx-auto max-w-6xl px-6">
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_1.05fr] lg:gap-16">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: EASE }}
              className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-muted-foreground"
            >
              {sv ? "Om Bilspektrum" : "About Bilspektrum"}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
              className="mb-6 font-display text-4xl font-bold leading-tight text-foreground lg:text-5xl"
            >
              {sv ? "En trivsam och trygg bilaffär" : "A pleasant and safe car purchase"}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
              className="mb-10 text-lg leading-relaxed text-muted-foreground"
            >
              {sv
                ? "År 1973 bildade Åke och Lars Oscarsson Bilspektrum AB. Företaget ägs och drivs numera av bröderna Eric och Per Oscarsson. Bilspektrum är ett genuint familjeföretag som ständigt strävar efter att ge nya och befintliga kunder den trygghet de har rätt att kräva. Hög servicenivå i alla led är vår främsta målsättning."
                : "In 1973 Åke and Lars Oscarsson founded Bilspektrum AB. The company is now owned and run by brothers Eric and Per Oscarsson. Bilspektrum is a genuine family business that constantly strives to give new and existing customers the security they have the right to expect. A high level of service in every part of the process is our foremost goal."}
            </motion.p>

            <dl className="mb-10 grid grid-cols-2 gap-x-8 gap-y-6">
              {FACTS.map((fact, index) => (
                <motion.div
                  key={fact.value}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1, ease: EASE }}
                  className="border-t border-foreground/15 pt-4"
                >
                  <dt className="font-display text-2xl font-bold text-primary lg:text-3xl">{fact.value}</dt>
                  <dd className="mt-1 text-sm leading-snug text-muted-foreground">{fact.label[language]}</dd>
                </motion.div>
              ))}
            </dl>

            <Link
              href="/om-oss"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              {sv ? "Läs mer om oss" : "Read more about us"}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <DealershipMap className="aspect-[4/3] lg:aspect-[5/6]" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
